import {
  MAX_TEXT_LENGTH,
  REWRITE_TIERS,
  countCharacters,
  countWords,
  type AiTool,
  type RewriteOptions,
  type Rewrites,
  type CustomTone,
  type Menu,
  type MenuSetting,
  type RewriteTier,
  type SavedInstruction,
  type Usage,
} from '@lyrmo/shared';
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ApiError } from '../../api/api-error';
import type { RewriteClient } from '../../api/client';
import { ariaShortcut, formatShortcut } from '@lyrmo/shared/shortcuts';
import { Button } from '../../components/Button';
import { Icon } from '../../components/Icon';
import { Toast } from '../../components/Toast';
import { useClipboard } from '../../hooks/useClipboard';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { motionAllowed, withViewTransition } from '../../lib/motion';
import { useTrack } from '../../lib/tracking-context';
import { AiToolsMenu, type ToolChoice, type ToolScope } from './AiToolsMenu';
import { InputPanel } from './InputPanel';
import { ResultsPanel } from './ResultsPanel';
import { RewriteOptionsBar } from './RewriteOptionsBar';
import { useAlternatives } from './useAlternatives';
import { useExplanations } from './useExplanations';
import { useRewrite } from './useRewrite';
import { SAVE_KEYS, useRewriteShortcuts } from './useRewriteShortcuts';
import { useSpeech } from './useSpeech';
import { useDraftHistory } from './useDraftHistory';
import { DictationControl } from './DictationControl';
import { ShortcutsDialog } from '../shortcuts/ShortcutsDialog';
import { useShortcuts } from '../shortcuts/useShortcuts';
import { DraftsDialog } from './DraftsDialog';
import { useSavedDrafts, type AccountDrafts, type SavedPlace } from './useSavedDrafts';
import { insertTranscript } from './insert-transcript';
import { copyFormatted } from './markdown-html';
import { locate, selectedPassage, type Placed } from './passage';
import { loadEnglishWords, loadedEnglishWords, mightBeEnglish } from './word-completion';
import { playEditFlight, playWordFlight } from './word-flight';
import type { DraftField, TextMark } from './WritingField';

// How long the text an AI tool made stays marked, as the mark fades: its animation's length.
const TOOL_DONE_MS = 1400;

export interface RewriteEditorProps {
  /** Pauses shortcuts and audio while the site's writer view is hidden. */
  readonly active?: boolean;
  readonly client: RewriteClient;
  readonly options: RewriteOptions;
  readonly onOptionsChange: (options: RewriteOptions) => void;
  /** The credits left today, once known. */
  readonly creditsLeft?: number | undefined;
  /** Receives the day's credit usage. */
  readonly onUsage?: ((usage: Usage) => void) | undefined;
  /** Offers plans at the credit limit; omitted where there's nothing more to offer. */
  readonly onSeePlans?: (() => void) | undefined;
  /** The writer's own tones, for the tone menu; null until they're known. */
  readonly customTones?: readonly CustomTone[] | null | undefined;
  /** Opens the writer's tones; omitted where there's no account to keep them. */
  readonly onManageTones?: (() => void) | undefined;
  /** Starts a new tone of the writer's own; omitted where there's no account to keep it. */
  readonly onNewTone?: (() => void) | undefined;
  /** The writer's saved instructions, for the instruction menu; null until they're known. */
  readonly savedInstructions?: readonly SavedInstruction[] | null | undefined;
  /** Opens Manage instructions; selection remains available without management. */
  readonly onManageInstructions?: (() => void) | undefined;
  /** Starts a new saved instruction: from one typed for one rewrite, or from nothing. */
  readonly onNewInstruction?: ((text: string) => void) | undefined;
  /** The writer's own AI tools, for the AI tools menu; null until they're known. */
  readonly aiTools?: readonly AiTool[] | null | undefined;
  /** Which choices in each menu, AI tools, tones, and instructions, the writer hid or pinned. */
  readonly menuSettings?: Readonly<Record<Menu, readonly MenuSetting[] | null>> | undefined;
  /** Opens the writer's AI tools; omitted where there's no account to keep them. */
  readonly onManageAiTools?: (() => void) | undefined;
  /** Starts a new AI tool of the writer's own; omitted where there's no account to keep it. */
  readonly onNewAiTool?: (() => void) | undefined;
  /** The draft to start with, such as text brought over from another app. */
  readonly initialText?: string | undefined;
  /** The current account; changing it cancels any recording in progress. Guests can dictate too. */
  readonly dictationAccount?: string | undefined;
  /** The writer's account, to keep drafts in; omitted where there's none. Files work anyway. */
  readonly accountDrafts?: AccountDrafts | undefined;
  /** Rewrites `initialText` as the editor opens, as when it's opened with text from a page. */
  readonly rewriteOnStart?: boolean | undefined;
  /** Versions of `initialText` made already, shown as the editor opens in place of rewriting. */
  readonly initialVersions?: Rewrites | undefined;
  /**
   * Puts a version where the draft came from, such as a text field on a web page: with the rest
   * of the draft around it when only a passage was rewritten. Each version's card gets a button
   * named `useVersionLabel` for it.
   */
  readonly onUseVersion?: ((text: string) => void) | undefined;
  readonly useVersionLabel?: string | undefined;
}

/**
 * The editor: owns the draft and coordinates the request, clipboard, and shortcuts. The page
 * it's on, the site or the desktop app, keeps the options and the account.
 */
export function RewriteEditor({
  active = true,
  client,
  options,
  onOptionsChange,
  creditsLeft,
  onUsage,
  onSeePlans,
  customTones,
  onManageTones,
  onNewTone,
  savedInstructions,
  onManageInstructions,
  onNewInstruction,
  aiTools,
  menuSettings,
  onManageAiTools,
  onNewAiTool,
  initialText,
  dictationAccount,
  accountDrafts,
  rewriteOnStart = false,
  initialVersions,
  onUseVersion,
  useVersionLabel,
}: RewriteEditorProps) {
  const {
    getAccount,
    createSpeech,
    transcribeAudio,
    getImageTextConfig,
    extractImageText,
    runTool,
  } = client;
  const usageTarget = useRef({ getAccount, onUsage });
  useLayoutEffect(() => {
    usageTarget.current = { getAccount, onUsage };
  }, [getAccount, onUsage]);
  const refreshUsage = useCallback(async () => {
    const target = usageTarget.current;
    if (!getAccount || !target.onUsage) return;
    try {
      const account = await getAccount();
      if (usageTarget.current.getAccount === getAccount)
        usageTarget.current.onUsage?.(account.usage);
    } catch {
      // A temporary refresh failure keeps the last known balance.
    }
  }, [getAccount]);
  const voice = useMemo(() => {
    return {
      createSpeech:
        createSpeech &&
        (async (...args: Parameters<typeof createSpeech>) => {
          try {
            return await createSpeech(...args);
          } finally {
            void refreshUsage();
          }
        }),
      transcribe:
        transcribeAudio &&
        (async (...args: Parameters<typeof transcribeAudio>) => {
          try {
            return await transcribeAudio(...args);
          } finally {
            void refreshUsage();
          }
        }),
    };
  }, [createSpeech, transcribeAudio, refreshUsage]);
  const readImage = useMemo(
    () =>
      extractImageText &&
      (async (...args: Parameters<typeof extractImageText>) => {
        if (getImageTextConfig) {
          const config = await getImageTextConfig(args[1]);
          if (!config.enabled)
            throw new ApiError('SERVICE_UNAVAILABLE', 'Image text detection is not configured.');
          if (!config.modes.includes(args[1]?.mode ?? 'text'))
            throw new ApiError('SERVICE_UNAVAILABLE', 'Describing images isn’t available yet.');
        }
        args[1]?.signal?.throwIfAborted();
        try {
          return await extractImageText(...args);
        } finally {
          void refreshUsage();
        }
      }),
    [extractImageText, getImageTextConfig, refreshUsage],
  );
  const draft = useDraftHistory(initialText);
  const { value: text, change: setText } = draft;
  const saved = useSavedDrafts(draft, accountDrafts);
  const [showDrafts, setShowDrafts] = useState(false);
  const { platform } = useShortcuts();
  const { state, cooldownSeconds, submit, revise, reset } = useRewrite(client, {
    onUsage,
    initial:
      initialText !== undefined && initialVersions
        ? { text: initialText, rewrites: initialVersions }
        : undefined,
  });
  // One voice for the draft and its versions; a new rewrite stops it.
  const speech = useSpeech(
    active ? voice.createSpeech : undefined,
    state.status === 'idle' ? 0 : state.id,
  );
  const explain = useExplanations(client);
  const suggest = useAlternatives(client);
  const clipboard = useClipboard<RewriteTier>(undefined, copyFormatted);
  const track = useTrack();
  const fieldRef = useRef<DraftField>(null);
  const [dictating, setDictating] = useState(false);
  // The AI tool formatting the draft, which is locked meanwhile, as while dictating.
  const [toolRun, setToolRun] = useState<ToolChoice | null>(null);
  const toolAbort = useRef<AbortController | null>(null);
  useEffect(
    () => () => {
      toolAbort.current?.abort();
    },
    [],
  );
  const locked = dictating || toolRun !== null;
  // What the tool is formatting, marked in the draft, then what it made, for a moment. Each
  // keeps its text, so it's marked only while the draft still has it there.
  const [toolMark, setToolMark] = useState<(TextMark & { readonly text: string }) | null>(null);
  useEffect(() => {
    if (toolMark?.state !== 'done') return;
    const timer = setTimeout(() => {
      setToolMark(null);
    }, TOOL_DONE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [toolMark]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  // Full screen, the editor has the window to itself; leaving it goes back to where the page was.
  const [fullScreen, setFullScreen] = useState(false);
  const pageScroll = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (!active) return;
    if (fullScreen) window.scrollTo(0, 0);
    else if (pageScroll.current !== null) {
      window.scrollTo(0, pageScroll.current);
      pageScroll.current = null;
    }
  }, [active, fullScreen]);
  function changeFullScreen(on: boolean) {
    if (on === fullScreen) return;
    if (on) pageScroll.current = window.scrollY;
    // The page cross-fades between its two layouts, rather than jumping from one to the other.
    withViewTransition('full-screen', () => {
      setFullScreen(on);
    });
  }
  const { t, describeError } = useI18n();
  // The version, or the AI tool's result, last made the draft, whose toast can take it back.
  const [taken, setTaken] = useState<{ id: number; message: string; text: string } | null>(null);
  // Why the last AI tool didn't change the draft.
  const [toolProblem, setToolProblem] = useState<{ id: number; message: string } | null>(null);
  const dismissToolProblem = useCallback(() => {
    setToolProblem(null);
  }, []);
  const takes = useRef(0);
  // Stable, so typing, which renders again, doesn't restart the toast's timer.
  const dismissTaken = useCallback(() => {
    setTaken(null);
  }, []);
  // How saving the draft went.
  const [draftNote, setDraftNote] = useState<{
    id: number;
    tone: 'success' | 'warning';
    message: string;
  } | null>(null);
  const dismissDraftNote = useCallback(() => {
    setDraftNote(null);
  }, []);
  function noteDraft(message: string, tone: 'success' | 'warning' = 'success') {
    takes.current += 1;
    setDraftNote({ id: takes.current, tone, message });
  }
  // Leaving with changes to a draft the writer saved asks first; nothing else is kept to lose.
  useEffect(() => {
    if (!saved.unsaved) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
    };
  }, [saved.unsaved]);
  // The version last put in place of the passage rewritten, by the rewrite it came from.
  const [placed, setPlaced] = useState<(Placed & { readonly id: number }) | null>(null);
  // Where dictation puts its words: the selection or caret it started at, as the field is
  // locked meanwhile. Null for the end, as when the writer hasn't been in the field yet.
  const dictationRange = useRef<{ start: number; end: number } | null>(null);
  const caretPlaced = useRef(false);
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;
    const place = () => {
      caretPlaced.current = true;
    };
    field.addEventListener('focus', place);
    return () => {
      field.removeEventListener('focus', place);
    };
  }, []);
  // What to select once the draft it's in is on screen: the caret after words dictated, or a
  // version put in place of a passage.
  const pendingSelection = useRef<readonly [number, number] | null>(null);
  useLayoutEffect(() => {
    const selection = pendingSelection.current;
    const field = fieldRef.current;
    if (!selection || !field) return;
    pendingSelection.current = null;
    const box = field.getBoundingClientRect();
    field.focus({ preventScroll: box.top >= 0 && box.bottom <= window.innerHeight });
    field.setSelectionRange(selection[0], selection[1]);
  });

  const busy = state.status === 'loading';
  // As each rewrite starts, however it was asked for, the draft's words take flight to the
  // versions' cards, which, new to the page, come in as they land (word-flight.ts). Before the
  // cards are first drawn, so they don't show early.
  const workspaceRef = useRef<HTMLDivElement>(null);
  const wasBusy = useRef(busy);
  useLayoutEffect(() => {
    if (busy && !wasBusy.current) {
      const cards = [
        ...(workspaceRef.current?.querySelectorAll<HTMLElement>('.result-card') ?? []),
      ];
      // Cards in place of the last rewrite's are there already (ResultsPanel).
      const enter = !cards.some((card) => card.classList.contains('is-replacing'));
      playWordFlight(fieldRef.current, cards, enter);
    }
    wasBusy.current = busy;
  }, [busy]);
  // A version taken to edit: its card's text, and where it is in the draft, for its words to
  // fly there once the draft is drawn with it (editVersion).
  const editing = useRef<{ card: HTMLElement; start: number; end: number } | null>(null);
  useLayoutEffect(() => {
    const taken = editing.current;
    if (!taken) return;
    editing.current = null;
    playEditFlight(taken.card, fieldRef.current, taken.start, taken.end);
  });
  const canSubmit =
    !locked &&
    !busy &&
    cooldownSeconds === 0 &&
    text.trim().length > 0 &&
    countCharacters(text) <= MAX_TEXT_LENGTH;

  // Where the passage rewritten is in the draft now, or the version put in its place.
  const passagePlace =
    state.status === 'success' && state.passage
      ? locate(text, [...(placed?.id === state.id ? [placed] : []), state.passage])
      : null;

  // Opened with text to rewrite, it starts at once. Too long a text waits, with the limit shown,
  // rather than fail at the server.
  const startRewrite = useEffectEvent(() => {
    if (!rewriteOnStart || initialVersions || !initialText?.trim()) return;
    if (countCharacters(initialText) > MAX_TEXT_LENGTH) return;
    void submit(initialText, options);
  });
  useEffect(() => {
    startRewrite();
  }, []);

  function rewrite() {
    if (!canSubmit) return;
    // With part of the draft selected, that part alone is rewritten, to fit back in.
    const field = fieldRef.current;
    const passage = field && selectedPassage(text, field.selectionStart, field.selectionEnd);
    void submit(passage?.text ?? text, options, passage ?? undefined);
  }

  function retry() {
    if (locked) return;
    // Retry the text that was submitted, even if the draft has changed since.
    if (state.status !== 'idle' && !busy && cooldownSeconds === 0)
      void submit(state.text, options, state.passage);
  }

  /** Clears the draft alone; the versions stay. Undo brings the text back. */
  function clearDraft() {
    if (locked) return;
    // The draft is swept away (input-panel.css).
    withViewTransition('clear', () => {
      if (speech.state?.source === 'draft') speech.stop();
      draft.replace('', null);
    });
    fieldRef.current?.focus();
  }

  /** Clears the draft and its versions together, to start afresh. */
  function clearAll() {
    if (locked) return;
    // The draft is swept away, the versions with it (input-panel.css).
    withViewTransition('clear', () => {
      speech.stop();
      reset();
      // A new draft, saved nowhere: Save asks where, rather than saving over the last one.
      draft.replace('', null);
    });
    fieldRef.current?.focus();
  }

  /** Clears the versions alone; the draft stays, to write on. */
  function clearVersions() {
    if (locked) return;
    speech.stop();
    reset();
    fieldRef.current?.focus();
  }

  /** Saves the draft where it's saved, or else asks where, as Save does in any app. */
  function saveDraft() {
    if (locked) return;
    const place = saved.place;
    if (!place || !saved.canSaveInPlace || !text.trim()) {
      setShowDrafts(true);
      return;
    }
    if (!saved.unsaved) {
      noteDraft(savedNote(place));
      return;
    }
    saved.saveInPlace().then(
      (done) => {
        if (done) noteDraft(savedNote(place));
        else setShowDrafts(true);
      },
      (error: unknown) => {
        noteDraft(
          error instanceof DOMException ? t.drafts.saveFailed : describeError(error),
          'warning',
        );
      },
    );
  }

  const savedNote = (place: SavedPlace) =>
    place.kind === 'account'
      ? t.drafts.savedToAccount(place.name)
      : t.drafts.savedToComputer(place.fileName);

  /** A draft opened, in place of the one before, whose versions go with it; Undo brings it back. */
  function openedDraft(place: SavedPlace) {
    speech.stop();
    reset();
    setToolMark(null);
    takes.current += 1;
    setTaken({ id: takes.current, message: t.drafts.opened(place.name), text: place.text });
  }

  /**
   * Makes a version the draft, to edit it or rewrite it again: in place of the passage it's a
   * version of, selected, when the rewrite was of part of the draft. The versions stay until the
   * next rewrite, and it's an edit like any other: Undo brings the text before it back.
   */
  function editVersion(index: number) {
    if (locked || state.status !== 'success') return;
    const tier = REWRITE_TIERS[index];
    const version = tier && state.rewrites[tier];
    if (!tier || !version) return;
    let next = version;
    if (state.passage) {
      if (!passagePlace) return;
      next = text.slice(0, passagePlace.start) + version + text.slice(passagePlace.end);
    }
    if (next === text) {
      fieldRef.current?.focus();
      return;
    }
    const applyVersion = () => {
      // Place the selection only when its new text is on the page, including during a transition.
      if (passagePlace && state.passage) {
        setPlaced({ id: state.id, start: passagePlace.start, text: version });
        pendingSelection.current = [passagePlace.start, passagePlace.start + version.length];
      }
      setText(next);
      setToolMark(null);
      takes.current += 1;
      setTaken({
        id: takes.current,
        message: t.results.nowEditing(t.tiers[tier].label),
        text: next,
      });
    };
    const focusDraft = () => {
      fieldRef.current?.focus({ preventScroll: true });
    };
    // Bringing an offscreen editor into view should scroll normally, without moving a snapshot.
    const box = fieldRef.current?.getBoundingClientRect();
    if (box && (box.top < 0 || box.bottom > window.innerHeight)) {
      applyVersion();
      fieldRef.current?.focus();
      return;
    }
    // Its words fly from its card, in sight, to their places in the draft (word-flight.ts); or
    // else the original, small text entrance, also used when pasting into the draft.
    const cards = workspaceRef.current?.querySelectorAll('.result-card');
    const card = cards?.[index]?.querySelector<HTMLElement>('.result-body');
    const place = card?.getBoundingClientRect();
    if (card && place && motionAllowed() && place.bottom > 0 && place.top < window.innerHeight) {
      const start = state.passage && passagePlace ? passagePlace.start : 0;
      editing.current = { card, start, end: start + version.length };
      applyVersion();
      focusDraft();
      return;
    }
    withViewTransition('paste', applyVersion, { after: focusDraft });
  }

  /** What an AI tool would cost now: for the selected passage, or else the whole draft. */
  function toolScope(): ToolScope {
    const field = fieldRef.current;
    const passage = field && selectedPassage(text, field.selectionStart, field.selectionEnd);
    return { selection: passage !== null, words: countWords(passage?.text ?? text) };
  }

  /**
   * Formats the selected passage, or the whole draft, with an AI tool, as one edit that Undo
   * takes back. A passage's result goes in its place, selected, ready for another tool.
   */
  async function applyTool(tool: ToolChoice) {
    if (!runTool || locked) return;
    const field = fieldRef.current;
    const passage = field && selectedPassage(text, field.selectionStart, field.selectionEnd);
    const target = passage?.text ?? text;
    if (!target.trim()) return;
    const draftText = text;
    const start = passage?.start ?? 0;
    const controller = new AbortController();
    toolAbort.current = controller;
    setToolRun(tool);
    setToolMark({
      start,
      end: start + target.length,
      state: 'working',
      since: performance.now(),
      text: target,
    });
    setToolProblem(null);
    const problem = (message: string) => {
      setToolMark(null);
      takes.current += 1;
      setToolProblem({ id: takes.current, message });
    };
    try {
      const result = await runTool(
        target,
        tool.instruction === undefined ? tool.id : { instruction: tool.instruction },
        {
          signal: controller.signal,
          ...(passage && { context: passage.context }),
        },
      );
      onUsage?.(result.usage);
      // The draft is locked meanwhile, but the keyboard's undo still reaches it.
      if (fieldRef.current?.value !== draftText) {
        problem(t.aiTools.draftChanged);
        return;
      }
      const next = passage
        ? draftText.slice(0, passage.start) +
          result.text +
          draftText.slice(passage.start + passage.text.length)
        : result.text;
      if (next === draftText) {
        setToolMark(null);
        return;
      }
      if (passage) pendingSelection.current = [passage.start, passage.start + result.text.length];
      else pendingSelection.current = [next.length, next.length];
      setText(next);
      setToolMark({
        start,
        end: start + result.text.length,
        state: 'done',
        since: performance.now(),
        text: result.text,
      });
      takes.current += 1;
      setTaken({ id: takes.current, message: t.aiTools.applied(tool.name), text: next });
    } catch (error) {
      if (controller.signal.aborted) return;
      if (error instanceof ApiError && error.usage) onUsage?.(error.usage);
      problem(describeError(error));
    } finally {
      if (toolAbort.current === controller) {
        toolAbort.current = null;
        setToolRun(null);
      }
    }
  }

  /** Stops the tool at work: the draft stays as it was, and its credits are given back. */
  function cancelTool() {
    toolAbort.current?.abort();
    toolAbort.current = null;
    setToolRun(null);
    setToolMark(null);
    fieldRef.current?.focus();
  }

  function dictationChanged(active: boolean) {
    if (active) {
      // The microphone would hear the draft being read.
      speech.stop();
      const field = fieldRef.current;
      dictationRange.current =
        field && caretPlaced.current
          ? { start: field.selectionStart, end: field.selectionEnd }
          : null;
      // To tell a sentence carried on in English by the time the words arrive.
      if (mightBeEnglish(text)) void loadEnglishWords().catch(() => undefined);
    }
    setDictating(active);
  }

  /** Puts dictated words where the caret was, or in place of the selection, as one edit. */
  function insertDictated(transcript: string) {
    const range = dictationRange.current ?? { start: text.length, end: text.length };
    const inserted = insertTranscript(text, transcript, range, loadedEnglishWords());
    if (inserted.text === text) {
      fieldRef.current?.focus();
      return;
    }
    pendingSelection.current = [inserted.caret, inserted.caret];
    if (!text.trim()) {
      track({ event: 'text', method: 'dictation', words: countWords(inserted.text) });
    }
    setText(inserted.text);
  }

  function copy(index: number) {
    const tier = REWRITE_TIERS[index];
    if (state.status !== 'success' || !tier || !state.rewrites[tier]) return;
    track({ event: 'version', version: tier, action: 'copy' });
    void clipboard.copy(tier, state.rewrites[tier]);
  }

  /** Puts a version where the draft came from: in place of the passage, with the rest around it. */
  function useVersion(index: number) {
    if (!onUseVersion || locked || state.status !== 'success') return;
    const tier = REWRITE_TIERS[index];
    const version = tier && state.rewrites[tier];
    if (!tier || !version) return;
    if (state.passage && !passagePlace) return;
    track({ event: 'version', version: tier, action: 'insert' });
    onUseVersion(
      state.passage && passagePlace
        ? text.slice(0, passagePlace.start) + version + text.slice(passagePlace.end)
        : version,
    );
  }

  useRewriteShortcuts({
    enabled: active,
    onSubmit: rewrite,
    onClear: clearDraft,
    onCopy: copy,
    onShowShortcuts: () => {
      setShowShortcuts(true);
    },
    onToggleFullScreen: () => {
      changeFullScreen(!fullScreen);
    },
    onExitFullScreen: fullScreen
      ? () => {
          changeFullScreen(false);
        }
      : undefined,
    onSave: saveDraft,
  });

  return (
    <div
      ref={workspaceRef}
      className={cx('workspace', active && fullScreen && 'workspace--full-screen workspace--fill')}
    >
      <InputPanel
        value={text}
        readOnly={locked}
        extractImageText={active ? readImage : undefined}
        importAccount={dictationAccount}
        onChange={(value, typing) => {
          if (!locked) setText(value, typing);
        }}
        onUndo={draft.undo}
        onRedo={draft.redo}
        canUndo={!locked && draft.canUndo}
        canRedo={!locked && draft.canRedo}
        onSubmit={rewrite}
        onClear={clearDraft}
        busy={busy}
        canSubmit={canSubmit}
        cooldownSeconds={cooldownSeconds}
        creditsLeft={creditsLeft}
        fieldRef={fieldRef}
        heading={
          saved.place && (
            <div className={cx('draft-place', saved.unsaved && 'is-unsaved')}>
              <button
                type="button"
                className="draft-place-name"
                title={
                  saved.place.kind === 'account'
                    ? t.drafts.inAccountTitle(saved.place.name)
                    : t.drafts.onComputerTitle(saved.place.fileName)
                }
                onClick={() => {
                  setShowDrafts(true);
                }}
              >
                <Icon name={saved.place.kind === 'account' ? 'cloud' : 'desktop'} />
                <span className="draft-place-label">{saved.place.name}</span>
              </button>
              <span className="draft-place-state">
                {saved.unsaved ? t.drafts.unsaved : t.drafts.saved}
              </span>
              {saved.unsaved && saved.canSaveInPlace && (
                <Button
                  disabled={locked}
                  aria-keyshortcuts={ariaShortcut(SAVE_KEYS, platform)}
                  title={t.drafts.saveTitle(formatShortcut(SAVE_KEYS, platform))}
                  onClick={saveDraft}
                >
                  {t.drafts.save}
                </Button>
              )}
            </div>
          )
        }
        headerActions={
          <>
            <Button
              className="clear-all-button"
              icon="close"
              title={t.editor.clearAllTitle}
              aria-label={t.editor.clearAll}
              disabled={locked || (!text && state.status === 'idle')}
              onClick={clearAll}
            >
              {t.editor.clearAll}
            </Button>
            <Button
              className="drafts-button"
              icon="folder"
              title={t.drafts.buttonTitle}
              aria-label={t.drafts.button}
              disabled={locked}
              onClick={() => {
                setShowDrafts(true);
              }}
            >
              {t.drafts.button}
            </Button>
          </>
        }
        pageWide={active}
        expanded={fullScreen}
        onExpandedChange={changeFullScreen}
        onShowShortcuts={() => {
          setShowShortcuts(true);
        }}
        speech={speech.state?.source === 'draft' ? speech.state : null}
        onSpeak={
          voice.createSpeech &&
          (() => {
            void speech.speak('draft', text);
          })
        }
        onStopSpeech={speech.stop}
        dictation={
          active &&
          client.transcribeAudio && (
            <DictationControl
              key={dictationAccount ?? 'guest'}
              transcribe={voice.transcribe}
              getConfig={client.getDictationConfig}
              creditsLeft={creditsLeft}
              onActiveChange={dictationChanged}
              onTranscript={insertDictated}
            />
          )
        }
        mark={toolMark !== null && text.startsWith(toolMark.text, toolMark.start) ? toolMark : null}
        aiTools={
          runTool && (
            <AiToolsMenu
              tools={aiTools ?? null}
              settings={menuSettings?.tools}
              running={toolRun}
              disabled={dictating || !text.trim()}
              measure={toolScope}
              onRun={(tool) => void applyTool(tool)}
              onCancel={cancelTool}
              onManage={onManageAiTools}
              onNew={onNewAiTool}
            />
          )
        }
        toolbar={
          <RewriteOptionsBar
            value={options}
            onChange={onOptionsChange}
            customTones={customTones}
            toneSettings={menuSettings?.tones}
            onManageTones={onManageTones}
            onNewTone={onNewTone}
            savedInstructions={savedInstructions}
            instructionSettings={menuSettings?.instructions}
            onManageInstructions={onManageInstructions}
            onNewInstruction={onNewInstruction}
            onInstructionDone={() => {
              fieldRef.current?.focus();
            }}
          />
        }
      />
      <ResultsPanel
        state={state}
        copied={clipboard.status}
        cooldownSeconds={cooldownSeconds}
        onCopy={copy}
        onRetry={retry}
        onEdit={editVersion}
        // A passage's versions go in its place, while the draft still has it.
        editable={state.status !== 'success' || !state.passage || passagePlace !== null}
        onSeePlans={onSeePlans}
        onExplain={explain}
        onSuggest={suggest}
        onRevise={revise}
        onClear={clearVersions}
        speech={voice.createSpeech && speech}
        onUse={onUseVersion && useVersion}
        useLabel={useVersionLabel}
        usable={!locked && (state.status !== 'success' || !state.passage || passagePlace !== null)}
      />
      <DraftsDialog
        open={showDrafts}
        onClose={() => {
          setShowDrafts(false);
        }}
        text={text}
        drafts={saved}
        account={
          accountDrafts && {
            ...accountDrafts,
            onSignIn: () => {
              setShowDrafts(false);
              accountDrafts.onSignIn();
            },
          }
        }
        onSaved={(place, how) => {
          noteDraft(
            how === 'download' && place.kind === 'file'
              ? t.drafts.downloaded(place.fileName)
              : savedNote(place),
          );
        }}
        onOpened={openedDraft}
      />
      <ShortcutsDialog
        open={showShortcuts}
        onClose={() => {
          setShowShortcuts(false);
          fieldRef.current?.focus();
        }}
      />
      {taken && (
        <Toast
          key={taken.id}
          tone="success"
          message={taken.message}
          onDismiss={dismissTaken}
          action={
            // Once the text has changed since, the editor's own Undo is the way back.
            !locked &&
            text === taken.text && (
              <Button
                className="bar-button"
                onClick={() => {
                  draft.undo();
                  setTaken(null);
                  fieldRef.current?.focus();
                }}
              >
                {t.editor.undo}
              </Button>
            )
          }
        />
      )}
      {toolProblem && (
        <Toast
          key={toolProblem.id}
          tone="warning"
          message={toolProblem.message}
          onDismiss={dismissToolProblem}
        />
      )}
      {draftNote && (
        <Toast
          key={draftNote.id}
          tone={draftNote.tone}
          message={draftNote.message}
          onDismiss={dismissDraftNote}
        />
      )}
    </div>
  );
}
